import { getFleetPerformanceAnalyticsList } from "../../../../Api/FleetPerformanceApi";
import { formatDateForAPI } from "../../../../Helper/Analytics/AnalyticsHelper";
import { formatDateRangeWithSmartYear } from "../../../../Helper/DatePicker/DateFormatters";

/**
 * One analytics list item as a table row: the entity attributes the request
 * asked for under metaInfo, and EVERY metric it returned, keyed by the metric's
 * own name.
 *
 * Every metric rather than a hand-picked few, so a column configured through org
 * meta - whose metric the select resolver has already asked for - renders its
 * value instead of an empty cell.
 *
 * A metric with no value (an average over no events, a BMS that does not report
 * it) becomes "-", not 0, so missing data is not shown as a real zero. This
 * matches buildAggregatedMetricsRow.
 */
export const toFleetTableRow = (item, index) => {
    const row = { "xLabel": item?.xLabel, "id": item?.xLabel || index, ...(item?.metaInfo || {}) };
    for (const metric of item?.metricsArray || []) {
        row[metric.type] = metric.value ?? "-";
    }
    return row;
};

/**
 * Common API call pattern for fetching table data for Fleet Performance
 * @param {Object} globalFilters - global filters
 * @param {Object} params - API parameters
 * @returns {Promise<{data: Array, total: number}>}
 */
export const getTableDataFromApi = async (globalFilters, params = {}) => {
    const listResponse = await getFleetPerformanceAnalyticsList(globalFilters, params);
    const listData = listResponse?.response?.data || [];
    const total = listResponse?.response?.paginationInfo?.total || listData.length;

    const transformedData = listData.map(toFleetTableRow);

    return { data: transformedData, total };
};

/**
 * Build base API parameters for drill-down queries
 */
export const buildBaseApiParams = (
    dataPoint,
    startDate,
    endDate,
    groupBy,
    select,
    metaInfo,
    filters = [],
    additionalParams = {}
) => {
    return {
        startDate: formatDateForAPI(dataPoint?.startDate || startDate),
        endDate: formatDateForAPI(dataPoint?.endDate || endDate),
        groupBy,
        granularity: null,
        select,
        metaInfo,
        filters,
        skipPagination: false,
        pageNum: additionalParams.pageNum || 1,
        pageCount: additionalParams.pageCount || 10,
        ...additionalParams,
    };
};

/**
 * Build API parameters for table actions (download, email, etc.)
 */
export const buildActionApiParams = (
    baseParams,
    actionType,
    rowSelectionKeys,
    selectAll,
    params = {}
) => {
    const apiParams = {
        actionType,
        ...baseParams,
        resourceIDs: selectAll ? [] : rowSelectionKeys,
        selectAll,
        columns: params.columns || [],
        columnOrder: params.columnOrder || [],
    };

    if (actionType === "mail") {
        apiParams.emailTo = params.emailTo;
        apiParams.emailCc = params.emailCc;
        apiParams.subject = params.subject;
        apiParams.body = params.body;
    }

    return apiParams;
};

/**
 * Format CSV filename with date range
 */
export const formatCSVFileName = (prefix, parts = [], startDate, endDate) => {
    const formattedStartDate = new Date(startDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });
    const formattedEndDate = new Date(endDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    const partsStr = parts.length > 0 ? `_${parts.join('_')}` : '';
    return `${prefix}${partsStr}_${formattedStartDate}-${formattedEndDate}.csv`;
};

/**
 * Build date category string for display
 */
export const buildDateCategory = (selectedDataPoint, startDate, endDate) => {
    return formatDateRangeWithSmartYear(
        selectedDataPoint?.startDate || startDate,
        selectedDataPoint?.endDate || endDate
    );
};
